import { CircularProgress } from "@mui/material"
import { Box } from "@mui/system"
import { useRouter } from "next/router"
import React, { useContext, useEffect } from "react"

import { ContentPage, Pages } from "../../../components/contentful"
import { PageTypes } from "../../../components/contentful/page"
import {
    getI18n,
    getPageContents,
} from "../../../components/contentful/pageProps"
import { Context } from "../../../context"
import { slugify } from "../../../util"
import { API } from "../../../util/api"
import { handleError } from "../../../util/handlers"
import { NextUtils } from "../../../util/next"
import FourOhFour from "../../404"

export const RenderMsoDmaPage = ({
    content,
    context,
    dispatch,
    msoName,
    name,
    stores,
    preSortedStoreList,
    type,
    preview,
}) => {
    useEffect(() => {
        dispatch({
            type: "STORES",
            payload: stores,
        })
    }, [dispatch, stores])

    const isDMA =
        type === PageTypes.DMA ||
        type === PageTypes.DMACollision ||
        type === PageTypes.DMAPaint

    let newContent
    if (isDMA && context?.dmaName !== name) {
        newContent = { dmaName: name, preSortedStoreList }
    }

    if (msoName && context?.msoName !== msoName) {
        newContent = { msoName }
    }

    return (
        <ContentPage
            {...{
                content,
                context: newContent || context,
                breadcrumbs: [],
                preview,
                type: Pages[type],
            }}
        />
    )
}

const LocationsStateArea = ({
    cities,
    content,
    msoName,
    name,
    notFound,
    slug,
    state,
    stores,
    preSortedStoreList,
    type,
    ...props
}) => {
    const router = useRouter()
    const { dispatch } = useContext(Context)
    const { preview } = props

    // If the page is not yet generated, this will be displayed
    // initially until getStaticProps() finishes running
    if (router.isFallback) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <CircularProgress />
            </Box>
        )
    }

    if ((!slug && !state) || notFound) return <FourOhFour />

    switch (type) {
        case PageTypes.State: {
            const breadcrumbs = [
                {
                    href: "/locations",
                    description: "Locations",
                },
                {
                    href: `/locations/${slugify(
                        state.stateAbbreviation.toLowerCase()
                    )}`,
                    description: state.stateName,
                },
            ]

            return (
                <ContentPage
                    {...{
                        content,
                        breadcrumbs,
                        preview,
                        context: {
                            ...state,
                            cities,
                        },
                        type: Pages[type],
                    }}
                />
            )
        }
        case PageTypes.MSO:
            return RenderMsoDmaPage({
                content,
                context: {
                    dmaName: null,
                    msoName,
                },
                dispatch,
                msoName,
                stores,
                preview,
                type: slug,
            })

        case PageTypes.DMA:
            return RenderMsoDmaPage({
                content,
                context: {
                    dmaName: name,
                    msoName: null,
                    preSortedStoreList,
                },
                dispatch,
                name,
                stores,
                type,
                preview,
            })
        default:
            return null
    }
}

export async function getStaticPaths({ defaultLocale }) {
    let paths = []

    try {
        const i18next = await getI18n({ locale: defaultLocale })
        const states = i18next.t("states")

        paths = states?.map(({ stateAbbreviation }) => ({
            params: {
                state: stateAbbreviation.toLowerCase(), //TODO should we support uppercase as well?
            },
        }))

        const dmaPaths = await NextUtils.getDmaPaths({ defaultLocale })
        const msoPaths = await NextUtils.getMsoPaths({ defaultLocale })
        //// pre-render the State, DMA and MSO pages
        paths = [...paths, ...dmaPaths, ...msoPaths]
    } catch (err) {
        console.log("Static Path Error [state]")
        handleError(err)
    }

    return {
        paths,
        // paths: [], // don't pre-render path; all pages should be rendered on-demand
        fallback: "blocking", // render the page on first request
    }
}

export async function getStaticProps({ defaultLocale, params, ...props }) {
    const { state: slug } = params
    const { preview } = props

    let name = null
    let msoName = null
    let notFound = false
    let type = null
    let pageType = {}
    let cities = null
    let stores = null
    let preSortedStoreList = false
    const lowercaseSlug = slug.toLowerCase()

    const i18next = await getI18n({ locale: defaultLocale })
    const states = i18next.t("states")
    const state =
        states.find(
            (state) => state.stateAbbreviation.toLowerCase() === lowercaseSlug
        ) || null

    if (state) {
        type = PageTypes.State
        pageType = Pages[type]
        cities = await API.getCities(state.stateName)
    } else if (lowercaseSlug.indexOf("-") >= 0) {
        if (lowercaseSlug.indexOf("-mso") >= 0) {
            type = PageTypes.MSO
            pageType = { name: lowercaseSlug }
            msoName = lowercaseSlug
            stores = await API.getMsoStores({ preview, msoUri: lowercaseSlug })
        } else {
            const dmaPages = await API.getDmaList(true)
            const hasDMA = dmaPages.find(({ url }) => url === lowercaseSlug)
            type = PageTypes.DMA
            pageType = Pages[type]

            if (hasDMA) {
                const {
                    name: dmaName,
                    stores: dmaStores,
                    sortedStoreList,
                } = await API.getDmaStores({
                    preview,
                    dmaUri: lowercaseSlug,
                })

                name = hasDMA.name
                notFound = !dmaName || !dmaStores?.length
                stores = dmaStores
                preSortedStoreList = sortedStoreList
            } else {
                name = lowercaseSlug
                notFound = true
                stores = []
            }
        }
    }

    return await getPageContents(
        {
            cities,
            msoName,
            name,
            notFound,
            slug: !state && lowercaseSlug.indexOf("-") >= 0 && lowercaseSlug,
            state,
            stores,
            preSortedStoreList,
            type,
            ...props,
        },
        pageType
    )
}

export default LocationsStateArea
